'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocketManagerContext } from '../providers/context';
import PricesWebSocketManager from '../library/prices-manager';
import USPricesWebSocketManager from '../library/us-prices-manager';
import NatsManager from '../library/nats-manager';
import type { LivePriceData } from '../types';
import {
  defaultLivePriceData,
  parseWebSocketMessage,
  parseNatsMessage,
  getEffectiveSource,
  isUSStocksOptions,
  type IndianStocksOptions,
  type USStocksOptions,
  type EffectiveSource,
} from './use-live-price-utils';
import { WS_TRACKING_EVENTS, NATS_EVENT_NAMES } from '../constants/nr-events';

/**
 * Options for useLivePrice hook - either Indian stocks or US stocks, not both
 */
export type UseLivePriceOptions = IndianStocksOptions | USStocksOptions;

/**
 * Hook to subscribe to live price updates for Indian or US stocks.
 *
 * @example
 * // Indian stocks - WebSocket (default)
 * const { price } = useLivePrice({
 *   webSocketPath: 'INDI00012:NSE:I',
 *   natsSocketPath: 'prices.NSE.RELIANCE',
 * });
 *
 * @example
 * // Indian stocks - NATS
 * const { price } = useLivePrice({
 *   natsSocketPath: 'prices.NSE.RELIANCE',
 *   source: 'nats',
 * });
 *
 * @example
 * // US stocks
 * const { price } = useLivePrice({
 *   usWebSocketPath: 'AAPL:NASDAQ:US',
 * });
 */
export function useLivePrice(options: UseLivePriceOptions): LivePriceData {
  const { indKey, enabled = true } = options;

  const isUSStocks = isUSStocksOptions(options);

  // Extract paths based on type
  const webSocketPath = isUSStocks ? undefined : options.webSocketPath;
  const natsSocketPath = isUSStocks ? undefined : options.natsSocketPath;
  const usWebSocketPath = isUSStocks ? options.usWebSocketPath : undefined;
  const source = isUSStocks ? 'websocket' : (options.source ?? 'websocket');

  const [priceData, setPriceData] =
    useState<LivePriceData>(defaultLivePriceData);
  const context = useSocketManagerContext();
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const trackEvent = context.config.analytics?.trackEvent;

  const effectiveSource: EffectiveSource = getEffectiveSource(
    isUSStocks,
    webSocketPath,
    natsSocketPath,
    source,
  );

  /**
   * Handler for WebSocket price messages
   */
  const handleWebSocketMessage = useCallback(
    (message: unknown) => {
      const parsed = parseWebSocketMessage(message, indKey);
      if (parsed) {
        setPriceData(parsed);
      }
    },
    [indKey],
  );

  /**
   * Handler for NATS price messages
   */
  const handleNatsMessage = useCallback(
    (data: unknown, error?: Error) => {
      if (error) {
        console.error('[useLivePrice] NATS error:', error);
        trackEvent?.(NATS_EVENT_NAMES.HOOK_ERROR, {
          hook: 'useLivePrice',
          natsSocketPath,
          error: error.message,
        });
        return;
      }
      setPriceData(parseNatsMessage(data));
    },
    [trackEvent, natsSocketPath],
  );

  /**
   * Effect: Subscribe to prices when connection is ready
   */
  useEffect(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Determine which path to use
    let socketPath: string | undefined;
    if (effectiveSource === 'us') {
      socketPath = usWebSocketPath;
    } else if (effectiveSource === 'websocket') {
      socketPath = webSocketPath;
    } else {
      socketPath = natsSocketPath;
    }

    if (!enabled || !socketPath) {
      setPriceData(defaultLivePriceData);
      return;
    }

    // US Stocks WebSocket subscription
    if (effectiveSource === 'us') {
      if (!context.isUSPricesConnected) return;

      const manager = USPricesWebSocketManager.getUSPricesWSInstance();
      if (!manager) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[useLivePrice] USPricesWebSocketManager not initialized',
          );
        }
        trackEvent?.(WS_TRACKING_EVENTS.HOOK_MANAGER_NOT_INITIALIZED, {
          hook: 'useLivePrice',
          source: 'us',
          socketPath,
        });
        return;
      }

      try {
        unsubscribeRef.current = manager.subscribe(
          socketPath,
          handleWebSocketMessage,
        );
      } catch (error) {
        console.error('[useLivePrice] Error subscribing to US prices:', error);
        trackEvent?.(WS_TRACKING_EVENTS.HOOK_SUBSCRIBE_ERROR, {
          hook: 'useLivePrice',
          source: 'us',
          socketPath,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    // Indian Stocks WebSocket subscription
    if (effectiveSource === 'websocket') {
      if (!context.isPricesConnected) return;

      const manager = PricesWebSocketManager.getPricesWSInstance();
      if (!manager) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[useLivePrice] PricesWebSocketManager not initialized');
        }
        trackEvent?.(WS_TRACKING_EVENTS.HOOK_MANAGER_NOT_INITIALIZED, {
          hook: 'useLivePrice',
          source: 'websocket',
          socketPath,
        });
        return;
      }

      try {
        unsubscribeRef.current = manager.subscribe(
          socketPath,
          handleWebSocketMessage,
        );
      } catch (error) {
        console.error('[useLivePrice] Error subscribing to prices:', error);
        trackEvent?.(WS_TRACKING_EVENTS.HOOK_SUBSCRIBE_ERROR, {
          hook: 'useLivePrice',
          source: 'websocket',
          socketPath,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    // NATS subscription
    if (effectiveSource === 'nats') {
      if (!context.isNatsConnected) return;

      try {
        const natsManager = NatsManager.getInstance();
        unsubscribeRef.current = natsManager.subscribe(
          socketPath,
          handleNatsMessage,
        );
      } catch (error) {
        console.error('[useLivePrice] Error subscribing to NATS:', error);
        trackEvent?.(NATS_EVENT_NAMES.HOOK_SUBSCRIBE_ERROR, {
          hook: 'useLivePrice',
          socketPath,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [
    enabled,
    webSocketPath,
    natsSocketPath,
    usWebSocketPath,
    effectiveSource,
    indKey,
    context.isPricesConnected,
    context.isUSPricesConnected,
    context.isNatsConnected,
    handleWebSocketMessage,
    handleNatsMessage,
    trackEvent,
  ]);

  /**
   * Effect: Update connection status
   */
  useEffect(() => {
    let isConnected = false;
    if (effectiveSource === 'us') {
      isConnected = context.isUSPricesConnected;
    } else if (effectiveSource === 'websocket') {
      isConnected = context.isPricesConnected;
    } else {
      isConnected = context.isNatsConnected;
    }

    setPriceData((prev) => ({ ...prev, isConnected }));
  }, [
    effectiveSource,
    context.isPricesConnected,
    context.isUSPricesConnected,
    context.isNatsConnected,
  ]);

  return priceData;
}

export default useLivePrice;
